import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {WideHeader} from '../../layout/wide';
import {Page} from '../../admin/publications/types/Page';
import pagesService from '../../admin/publications/services/pagesService';
import {getValueWithFallback} from '../../common/types/StringTranslations';
import {Section} from '../../admin/artisticobjects/types/Section';
import {DEFAULT_LANGUAGE} from '../../common/i18n/config';
import {handleError} from '../../common/utils/errorHandling';
import './PageView.scss';

const filterSectionsByLanguage = (sections: Section[], currentLanguage: string): Section[] => {
  const sectionsByType = new Map<string, Section[]>();

  sections.forEach(section => {
    const typeAlias = section.type.alias;
    if (!sectionsByType.has(typeAlias)) {
      sectionsByType.set(typeAlias, []);
    }
    sectionsByType.get(typeAlias)!.push(section);
  });

  const filteredSections: Section[] = [];

  sectionsByType.forEach((sectionsOfType) => {
    const currentLangSection = sectionsOfType.find(s => s.language === currentLanguage);
    const defaultLangSection = sectionsOfType.find(s => s.language === DEFAULT_LANGUAGE);
    const firstSection = sectionsOfType[0];

    const selectedSection = currentLangSection || defaultLangSection || firstSection;
    if (selectedSection) {
      filteredSections.push(selectedSection);
    }
  });

  return filteredSections;
};

const PageView: React.FC = () => {
  const {alias} = useParams<{ alias: string }>();
  const {t, i18n} = useTranslation();
  const [page, setPage] = useState<Page | null>(null);

  useEffect(() => {
    if (alias) {
      fetchPage(alias);
    }
  }, [alias]);

  const fetchPage = async (pageAlias: string): Promise<void> => {
    await handleError(
      async () => {
        const pageData = await pagesService.getPageByAlias(pageAlias);
        setPage(pageData);
      },
      t('common.errors.fetchingPage')
    );
  };

  if (!page) {
    return null;
  }

  const title = getValueWithFallback(page.title, i18n.language) || '';
  const sections = filterSectionsByLanguage(page.sections || [], i18n.language);

  return (
    <>
      <WideHeader title={title} narrow={true}/>

      <section className="bg-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-gray-700 leading-relaxed space-y-6">
              {sections.map((section) => (
                <div key={section.id}>
                  <div
                    className="page-content"
                    dangerouslySetInnerHTML={{__html: section.content}}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PageView;

